/* ==========================================================================
   viblii — Judge.me "Verified on Amazon" badge swap
   --------------------------------------------------------------------------
   Rewrites Judge.me's "Review collected from another provider" transparency
   badge to "Verified on Amazon" on imported reviews.

   Assumption baked in: every imported review in viblii's Judge.me account is
   an Amazon import. If viblii ever imports from Etsy / Google / Shop App,
   those get their own dedicated Judge.me transparency badges and won't match
   the selector below — safe by design. But if they ever CSV-import from a
   non-Amazon source (which would also render as "from another provider"),
   the label would become factually wrong. Revisit this file before enabling
   any non-Amazon import.

   DOM targets confirmed on 2026-04-24:

   Judge.me runs TWO widget versions side-by-side on the viblii storefront:

   1. NEW widget (Vue-based, primary on viblii PDPs):
      - Tag:   <div class="jm-text" style="... color: var(--jm-transparency-badge-color);">
      - Text:  "Review collected from another provider"
      - Distinguishing marker: the CSS variable --jm-transparency-badge-color
        in the inline style attribute. The "jm-text" class alone is generic
        (used all over the new widget), so we combine it with the style
        attribute filter to avoid false positives.
      - Root container: varies — we observe document.body to catch both.

   2. LEGACY widget (older installs, fallback):
      - Class: .jdgm-rev__transparency-badge
      - Text:  "Review collected from another provider"
      - Root:  #judgeme_product_reviews

   Belt-and-braces signal for reviews WITH photos: Judge.me's API returns
   picture_urls hosted on m.media-amazon.com for imported Amazon reviews.
   We don't check that here (would require a JSON fetch) — the transparency
   badge alone is sufficient because viblii confirmed Amazon-only imports.

   Rollback: delete this file + remove the <script> tag from theme.liquid.
   ========================================================================== */
(function () {
  'use strict';

  var ORIGINAL_TEXTS = [
    'Review collected from another provider',
    // Guard against minor wording tweaks from Judge.me-side updates:
    'Review from another provider',
    'Review collected from another platform'
  ];
  var NEW_TEXT = 'Verified on Amazon';
  var MARKER_CLASS = 'viblii-amazon-badge';

  // Selectors for both widget versions. The new-widget selector uses
  // attribute-contains on inline style to match only transparency badges,
  // not arbitrary .jm-text elements elsewhere in the widget.
  var SELECTORS = [
    '.jm-text[style*="--jm-transparency-badge-color"]',  // new widget
    '.jdgm-rev__transparency-badge'                       // legacy widget
  ];
  var COMBINED_SELECTOR = SELECTORS.join(', ');

  function isMatchingBadge(el) {
    if (!el || el.classList.contains(MARKER_CLASS)) return false;
    var text = (el.textContent || '').trim();
    for (var i = 0; i < ORIGINAL_TEXTS.length; i++) {
      if (text.indexOf(ORIGINAL_TEXTS[i]) === 0) return true;
    }
    return false;
  }

  function swapBadge(el) {
    if (!isMatchingBadge(el)) return;
    el.textContent = NEW_TEXT;
    el.setAttribute('aria-label', NEW_TEXT);
    el.setAttribute('title', NEW_TEXT);
    el.classList.add(MARKER_CLASS);
  }

  function sweep(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll(COMBINED_SELECTOR);
    for (var i = 0; i < nodes.length; i++) swapBadge(nodes[i]);
  }

  function init() {
    // Initial pass — catches reviews already in the DOM.
    sweep(document);

    // Ongoing watch — catches client-side review list injection, "Load more"
    // pagination, sort changes, Vue re-renders, etc. Observer stays live
    // for the full page session by design. We watch document.body because
    // the new widget may mount outside #judgeme_product_reviews.
    if (typeof MutationObserver !== 'function') return;

    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () {
        pending = false;
        sweep(document);
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
